import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import { Button, useNotify } from 'react-admin';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SendIcon from '@mui/icons-material/Send';
import { Switch } from '../../components/switch/Switch.tsx';
import PushService, { type PushCampaign, type PushCopy, type VenuePair } from '../../services/push/pushService.ts';

const pushService = new PushService();

/** The languages the customer app ships. Hebrew first: most customers read it. */
const LANGUAGES = ['HEB', 'ENG', 'RUS', 'GRE', 'ARA', 'GER'] as const;
const RTL = new Set(['HEB', 'ARA']);

const euro = (n: number | null | undefined) => `€${Number(n ?? 0).toFixed(2)}`;

/** "720" -> "12:00", so nobody has to think in minutes past midnight. */
const windowsToLabel = (windows: string): string =>
    windows
        .split(',')
        .map((w) => Number(w.trim()))
        .filter((m) => !Number.isNaN(m))
        .map((m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`)
        .join(', ') || '—';

/** The placeholders the backend fills, filled here with the same values. */
const fill = (text: string, sample: { restaurant: string; otherVenue: string }) =>
    text
        .replaceAll('{otherVenue}', sample.otherVenue)
        .replaceAll('{restaurant}', sample.restaurant)
        .replaceAll('{attraction}', 'Paradox Museum')
        .replaceAll('{minutes}', '8')
        .replaceAll('{firstName}', 'Yossi');

/**
 * One line on when a campaign goes out, in its own terms: a nudge after an
 * order, a welcome after landing, a cart after ten minutes. The timing
 * fields below the line are the same for all of them; the words are not.
 */
const whenItSends = (campaign: PushCampaign): string => {
    const stops = `stops after ${campaign.expireAfterHours}h`;
    const at = windowsToLabel(campaign.sendWindows);
    const step = campaign.sequenceStep ?? 1;
    switch (campaign.triggerType) {
        case 'TRIP_ARRIVED':
            return step <= 1
                ? `Welcome · ${campaign.delayHours > 0 ? `${campaign.delayHours} h` : 'the first allowed moment'} after the phone switches to Cyprus time · ${stops}`
                : `Day ${step - 1} of the stay · lunch or dinner, whichever they usually order · skipped after an order that day · ${stops}`;
        case 'PAYMENT_PENDING':
            return `${campaign.delayMinutes ?? 7} min after the payment link opens, if still unpaid · ${stops}`;
        case 'CART_FILLED':
            return `${campaign.delayMinutes ?? 10} min after a cart is filled, if no order follows · replaced by the checkout nudge · ${stops}`;
        case 'CHECKOUT_REACHED':
            return `${campaign.delayMinutes ?? 10} min after the checkout screen (fees shown), if no order follows · replaced by the payment nudge · ${stops}`;
        case 'TRIP_PLAN_TOUR':
            return `${at} on the day of a trip-plan attraction (tours starting before 09:30 are skipped) · ${stops}`;
        case 'LOCAL_INACTIVE':
            return step <= 1
                ? `Residents with no order for 21 days · sends at ${at} · once in two months at most · ${stops}`
                : `${Math.round(campaign.delayHours / 24)} days after the reminder, if still no order · grants €3 credit for 14 days as it goes out · ${stops}`;
        case 'ORDER_DELIVERED_LOCAL_FIRST':
            return `A resident's first delivered order · the same hour one week later · ${stops}`;
        default:
            return `Nudge ${step} · sends at ${at} · ${campaign.delayHours === 0 ? 'first window after the order' : `${campaign.delayHours}h after the order`} · ${stops}`;
    }
};

/** "Finishing soon": sent ~30 min before a booked attraction ends, not on a meal window. */
const FINISHING_SOON = 'ATTRACTION_FINISHING';
const isFinishingSoon = (campaign: PushCampaign) =>
    campaign.triggerType === FINISHING_SOON || campaign.campaignKey.startsWith('finishing_soon');

/**
 * Staging test override for "finishing soon": send the message for one trip
 * plan item now. The server still applies the other rules (quiet hours,
 * Shabbat, venue open, ordered in the last 2 h) - and the 20 h cap unless
 * "ignore cap" is ticked.
 */
const FinishingSoonFireCard = ({ onFired }: { onFired: () => void }) => {
    const notify = useNotify();
    const [itemId, setItemId] = useState('');
    const [ignoreCap, setIgnoreCap] = useState(true);
    // Quiet hours (22:00-09:00) and Shabbat, for testing at night on staging.
    const [ignoreQuietHours, setIgnoreQuietHours] = useState(false);
    const [firing, setFiring] = useState(false);
    const [result, setResult] = useState<string | null>(null);

    const fire = async () => {
        const id = Number(itemId);
        if (!id) {
            notify('Enter the trip plan item id', { type: 'warning' });
            return;
        }
        setFiring(true);
        try {
            const text = await pushService.fireFinishingSoon(id, ignoreCap, ignoreQuietHours);
            setResult(text);
            notify(text, { type: 'info', autoHideDuration: 8000 });
            onFired();
        } catch (e: any) {
            const text = e?.response?.data?.error ?? e?.response?.data?.message ?? 'Could not fire it';
            setResult(String(text));
            notify(String(text), { type: 'error' });
        } finally {
            setFiring(false);
        }
    };

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6">Fire "finishing soon" now (test)</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 680 }}>
                    Sends the message for one trip plan item as if its attraction ends in 30 minutes: a push to the
                    app, or an email when the customer has no app. Admin only; meant for staging tests.
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <TextField
                        id="finishing-soon-item"
                        label="Trip plan item id"
                        value={itemId}
                        onChange={(e) => setItemId(e.target.value.replace(/\D/g, ''))}
                        size="small"
                        sx={{ width: 180 }}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={ignoreCap} onChange={(e) => setIgnoreCap(e.target.checked)} />}
                        label="Ignore the 20 h cap"
                    />
                    <FormControlLabel
                        control={<Checkbox checked={ignoreQuietHours} onChange={(e) => setIgnoreQuietHours(e.target.checked)} />}
                        label="Ignore quiet hours / Shabbat"
                    />
                    <Button
                        label={firing ? 'Sending…' : 'Fire now'}
                        onClick={fire}
                        disabled={firing}
                        startIcon={<SendIcon />}
                        variant="contained"
                    />
                </Stack>
                {result && (
                    <Typography variant="body2" sx={{ mt: 1.5, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>{result}</Typography>
                )}
            </CardContent>
        </Card>
    );
};

/**
 * "12:00" -> "720", and the inverse of windowsToLabel.
 *
 * Accepts a comma-separated list so a campaign already set to two windows
 * round-trips instead of showing as invalid the moment the page loads.
 */
const labelToWindows = (label: string): string | null => {
    const parts = label.split(',').map((x) => x.trim()).filter((x) => x !== '');
    if (parts.length === 0) return null;

    const minutes: number[] = [];
    for (const part of parts) {
        const m = /^(\d{1,2}):(\d{2})$/.exec(part);
        if (!m) return null;
        const h = Number(m[1]);
        const min = Number(m[2]);
        if (h > 23 || min > 59) return null;
        minutes.push(h * 60 + min);
    }
    return minutes.join(',');
};

const copyFor = (campaign: PushCampaign, language: string): PushCopy =>
    campaign.copy.find((c) => c.language === language) ?? { language, title: '', body: '' };

/**
 * What the customer's phone will show. Not decoration: a line that reads well in
 * a text box can wrap badly on a lock screen, and Hebrew has to be seen
 * right-to-left to be judged at all.
 */
const LockScreenPreview = ({ title, body, language }: { title: string; body: string; language: string }) => (
    <Box
        sx={{
            borderRadius: 3,
            p: 2,
            bgcolor: 'rgba(30,30,32,0.92)',
            color: '#fff',
            maxWidth: 380,
            direction: RTL.has(language) ? 'rtl' : 'ltr'
        }}
    >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, opacity: 0.7 }}>
            <Box sx={{ width: 16, height: 16, borderRadius: 0.8, bgcolor: '#1FA394' }} />
            <Typography variant="caption">VELLO · now</Typography>
        </Stack>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
            {title || '—'}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.3 }}>
            {body || '—'}
        </Typography>
    </Box>
);

/**
 * Which two venues point at each other.
 *
 * <p>Here rather than in a migration because location ids differ between
 * staging and production, and here rather than on the Settings page because
 * this is the one switch that decides whether any of the campaigns below do
 * anything at all - with no pair chosen, nothing is ever queued.
 */
const VenuePairCard = ({ pair, onSaved }: { pair: VenuePair; onSaved: () => void }) => {
    const notify = useNotify();
    const [first, setFirst] = useState<number | ''>(pair.firstLocationId ?? '');
    const [second, setSecond] = useState<number | ''>(pair.secondLocationId ?? '');
    const [saving, setSaving] = useState(false);

    const dirty = (first === '' ? null : first) !== pair.firstLocationId
        || (second === '' ? null : second) !== pair.secondLocationId;
    const sameVenue = first !== '' && first === second;
    const halfSet = (first === '') !== (second === '');

    const label = (v: { name: string; city: string | null }) => (v.city ? `${v.name} — ${v.city}` : v.name);

    const save = async () => {
        setSaving(true);
        try {
            await pushService.setVenuePair(first === '' ? null : first, second === '' ? null : second);
            notify('Venue pair saved', { type: 'success' });
            onSaved();
        } catch {
            notify('Could not save the venue pair', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6">The pair</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 680 }}>
                    A customer who orders from one of these is nudged toward the other. Pick two venues in the same
                    city — a nudge across cities points at an hour's drive. Leave both empty to stop every
                    cross-venue push, whatever the campaigns below say.
                </Typography>

                {!pair.firstLocationId && !pair.secondLocationId && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        No pair chosen, so nothing is being queued. Pick two venues and save.
                    </Alert>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                    <FormControl size="small" sx={{ minWidth: 260 }}>
                        <InputLabel id="venue-first-label">First venue</InputLabel>
                        <Select
                            labelId="venue-first-label"
                            id="venue-first"
                            label="First venue"
                            value={first}
                            onChange={(e) => setFirst(e.target.value === '' ? '' : Number(e.target.value))}
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {pair.options.map((v) => (
                                <MenuItem key={v.id} value={v.id}>
                                    {label(v)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 260 }}>
                        <InputLabel id="venue-second-label">Second venue</InputLabel>
                        <Select
                            labelId="venue-second-label"
                            id="venue-second"
                            label="Second venue"
                            value={second}
                            onChange={(e) => setSecond(e.target.value === '' ? '' : Number(e.target.value))}
                        >
                            <MenuItem value="">
                                <em>None</em>
                            </MenuItem>
                            {pair.options.map((v) => (
                                <MenuItem key={v.id} value={v.id}>
                                    {label(v)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        label={saving ? 'Saving…' : 'Save pair'}
                        onClick={save}
                        disabled={!dirty || saving || sameVenue || halfSet}
                        variant="contained"
                    />
                </Stack>

                {sameVenue && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                        Pick two different venues.
                    </Typography>
                )}
                {halfSet && (
                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                        Choose both, or clear both.
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

/** Sample values for the preview. The real pair's names when it is set, so what
 *  is previewed is what will actually be sent. */
type SampleNames = { restaurant: string; otherVenue: string };

const CampaignCard = ({
    campaign,
    sample,
    onSaved
}: {
    campaign: PushCampaign;
    sample: SampleNames;
    onSaved: () => void;
}) => {
    const notify = useNotify();
    const [language, setLanguage] = useState<string>('HEB');
    const [draft, setDraft] = useState<Record<string, PushCopy>>({});
    const [saving, setSaving] = useState(false);
    const [testCustomerId, setTestCustomerId] = useState('');
    const [testing, setTesting] = useState(false);
    const [sendAt, setSendAt] = useState(windowsToLabel(campaign.sendWindows));
    const [delayHours, setDelayHours] = useState(String(campaign.delayHours));
    const [expireHours, setExpireHours] = useState(String(campaign.expireAfterHours));
    const [savingTiming, setSavingTiming] = useState(false);

    const windows = labelToWindows(sendAt);
    const timingDirty = windows !== campaign.sendWindows
        || Number(delayHours) !== campaign.delayHours
        || Number(expireHours) !== campaign.expireAfterHours;
    const timingValid = windows !== null && delayHours !== '' && expireHours !== '';

    const saveTiming = async () => {
        if (!timingValid) {
            notify('Send time must look like 12:00', { type: 'warning' });
            return;
        }
        setSavingTiming(true);
        try {
            await pushService.update(campaign.id, {
                sendWindows: windows as string,
                delayHours: Number(delayHours),
                expireAfterHours: Number(expireHours)
            });
            notify('Timing saved', { type: 'success' });
            onSaved();
        } catch {
            notify('Could not save the timing', { type: 'error' });
        } finally {
            setSavingTiming(false);
        }
    };

    // The stored timing can change under us - another admin, or our own save
    // being rejected - and the fields must follow it rather than keep showing
    // what was typed.
    useEffect(() => {
        setSendAt(windowsToLabel(campaign.sendWindows));
        setDelayHours(String(campaign.delayHours));
        setExpireHours(String(campaign.expireAfterHours));
    }, [campaign.sendWindows, campaign.delayHours, campaign.expireAfterHours]);

    const current = draft[language] ?? copyFor(campaign, language);
    const dirty = Object.entries(draft).some(([lang, c]) => {
        const saved = copyFor(campaign, lang);
        return c.title !== saved.title || c.body !== saved.body;
    });

    const setField = (field: 'title' | 'body', value: string) =>
        setDraft((prev) => ({ ...prev, [language]: { ...current, language, [field]: value } }));

    const save = async () => {
        setSaving(true);
        try {
            await pushService.update(campaign.id, { copy: Object.values(draft) });
            notify('Copy saved', { type: 'success' });
            setDraft({});
            onSaved();
        } catch {
            notify('Could not save the copy', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (isActive: boolean) => {
        try {
            await pushService.update(campaign.id, { isActive });
            notify(isActive ? 'Campaign is live' : 'Campaign switched off', { type: 'info' });
            onSaved();
        } catch {
            notify('Could not change the campaign', { type: 'error' });
        }
    };

    const sendTest = async () => {
        const id = Number(testCustomerId);
        if (!id) {
            notify('Enter the customer id to send the test to', { type: 'warning' });
            return;
        }
        setTesting(true);
        try {
            const result = await pushService.sendTest(campaign.id, id, language);
            notify(result, { type: 'info', autoHideDuration: 6000 });
        } catch {
            notify('Test push failed — check the customer has opened the app', { type: 'error' });
        } finally {
            setTesting(false);
        }
    };

    const { stats } = campaign;
    const finishing = isFinishingSoon(campaign);

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                    <Box>
                        <Typography variant="h6">{campaign.name}</Typography>
                        {finishing ? (
                            <Typography variant="body2" color="text.secondary">
                                ~30 min before a booked attraction ends · {campaign.campaignKey.endsWith('delivery')
                                    ? 'delivery to the hotel (no dine-in venue open nearby)'
                                    : 'delivery or dine in at the nearest dine-in venue'} · push, or email without the app
                            </Typography>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {whenItSends(campaign)}
                            </Typography>
                        )}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                            size="small"
                            label={campaign.isActive ? 'Live' : 'Off'}
                            color={campaign.isActive ? 'success' : 'default'}
                        />
                        <Switch checked={campaign.isActive} onChange={(e: any) => toggleActive(e.target.checked)} />
                    </Stack>
                </Stack>

                <Stack direction="row" spacing={3} sx={{ mt: 2, flexWrap: 'wrap' }}>
                    <Stat label="Sent" value={String(stats.sent)} />
                    {stats.tapped != null && (
                        <Stat label="Tapped" value={String(stats.tapped)} hint="opened the app from the notification" />
                    )}
                    {stats.emailed != null && (
                        <Stat label="By email" value={String(stats.emailed)} hint="no app - sent as an email instead" />
                    )}
                    <Stat label="Orders after" value={String(stats.attributedOrders)} />
                    <Stat label="Revenue" value={euro(stats.attributedRevenue)} />
                    <Stat label="Ordered first" value={String(stats.cancelledBecauseOrdered)} hint="cancelled — they ordered before we asked" />
                    <Stat label="Not sent" value={String(stats.cancelledOther)} hint="venue closed, out of area, daily cap" />
                </Stack>

                {!finishing && <>
                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    When it sends
                </Typography>
                <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                    <TextField
                        id={`sendat-${campaign.id}`}
                        label="Send at"
                        value={sendAt}
                        onChange={(e) => setSendAt(e.target.value)}
                        size="small"
                        sx={{ width: 120 }}
                        error={sendAt !== '' && windows === null}
                        helperText="local time, 24h"
                    />
                    <TextField
                        id={`delay-${campaign.id}`}
                        label="Not before"
                        value={delayHours}
                        onChange={(e) => setDelayHours(e.target.value.replace(/\D/g, ''))}
                        size="small"
                        sx={{ width: 120 }}
                        helperText="hours after delivery"
                    />
                    <TextField
                        id={`expire-${campaign.id}`}
                        label="Give up after"
                        value={expireHours}
                        onChange={(e) => setExpireHours(e.target.value.replace(/\D/g, ''))}
                        size="small"
                        sx={{ width: 130 }}
                        helperText="hours after delivery"
                    />
                    <Button
                        label={savingTiming ? 'Saving…' : 'Save timing'}
                        onClick={saveTiming}
                        disabled={!timingDirty || savingTiming || !timingValid}
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                    />
                </Stack>
                </>}

                <Divider sx={{ my: 2 }} />

                <Tabs value={language} onChange={(_, v) => setLanguage(v)} variant="scrollable" scrollButtons="auto">
                    {LANGUAGES.map((lang) => (
                        <Tab key={lang} value={lang} label={lang} />
                    ))}
                </Tabs>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ mt: 2 }}>
                    <Stack spacing={2} sx={{ flex: 1, minWidth: 260 }}>
                        <TextField
                            id={`title-${campaign.id}-${language}`}
                            label="Title"
                            value={current.title}
                            onChange={(e) => setField('title', e.target.value)}
                            inputProps={{ dir: RTL.has(language) ? 'rtl' : 'ltr', maxLength: 120 }}
                            fullWidth
                        />
                        <TextField
                            id={`body-${campaign.id}-${language}`}
                            label="Body"
                            value={current.body}
                            onChange={(e) => setField('body', e.target.value)}
                            inputProps={{ dir: RTL.has(language) ? 'rtl' : 'ltr', maxLength: 400 }}
                            multiline
                            minRows={2}
                            fullWidth
                            helperText={finishing
                                ? '{attraction} {restaurant} {minutes} {firstName} are filled in per customer'
                                : '{otherVenue} {restaurant} {firstName} are filled in per customer'}
                        />
                        <Box>
                            <Button label={saving ? 'Saving…' : 'Save copy'} onClick={save} disabled={!dirty || saving} variant="contained" />
                        </Box>
                    </Stack>

                    <Stack spacing={2} sx={{ flex: 1, minWidth: 260 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                            On a lock screen
                        </Typography>
                        <LockScreenPreview
                            title={fill(current.title, sample)}
                            body={fill(current.body, sample)}
                            language={language}
                        />
                        {!finishing && <>
                        <Divider />
                        <Typography variant="subtitle2" color="text.secondary">
                            Send it to a real phone
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                            <TextField
                                id={`test-${campaign.id}`}
                                label="Customer id"
                                value={testCustomerId}
                                onChange={(e) => setTestCustomerId(e.target.value.replace(/\D/g, ''))}
                                size="small"
                                sx={{ width: 160 }}
                            />
                            <Button
                                label={testing ? 'Sending…' : 'Send test'}
                                onClick={sendTest}
                                disabled={testing}
                                startIcon={<SendIcon />}
                                variant="outlined"
                            />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            Ignores the daily cap and the schedule. Uses the saved copy, so save first.
                        </Typography>
                        </>}
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};

const Stat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
    <Box>
        <Typography variant="h6" sx={{ lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
            {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
            {label}
        </Typography>
        {hint && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', opacity: 0.7 }}>
                {hint}
            </Typography>
        )}
    </Box>
);

/**
 * The Notifications page.
 *
 * <p>Three nudges toward the other Moses venue, because a customer who has used
 * both orders 4.16 times against 1.61 for one. Nothing here picks recipients or
 * schedules a send - the rules do that. The only decisions are the words and
 * whether a campaign is on.
 */
const NotificationsPage = () => {
    const notify = useNotify();
    const [campaigns, setCampaigns] = useState<PushCampaign[] | null>(null);
    const [pair, setPair] = useState<VenuePair | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const [list, venuePair] = await Promise.all([pushService.list(), pushService.getVenuePair()]);
            setCampaigns(list);
            setPair(venuePair);
        } catch {
            notify('Could not load the campaigns', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [notify]);

    useEffect(() => {
        void load();
    }, [load]);

    // The preview names the real pair, so what is on screen is what will be sent.
    const nameOf = (id: number | null | undefined) =>
        pair?.options.find((o) => o.id === id)?.name ?? '';
    const sample: SampleNames = {
        restaurant: nameOf(pair?.firstLocationId) || 'the venue they ordered from',
        otherVenue: nameOf(pair?.secondLocationId) || 'the other venue'
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <NotificationsActiveIcon color="primary" />
                <Typography variant="h5">Notifications</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 680 }}>
                A customer who has ordered from both Moses venues places 4.16 orders; one venue only, 1.61. The
                cross-venue nudges point Cafe customers at the Meat for dinner and Meat customers at the Cafe for
                breakfast. They stop if the customer orders, if the venue is closed, or once the visitor's window has
                passed. At most one marketing message per customer every 20 hours, across all campaigns.
            </Typography>

            {pair && (
                <VenuePairCard
                    key={`${pair.firstLocationId}-${pair.secondLocationId}`}
                    pair={pair}
                    onSaved={load}
                />
            )}

            {(campaigns ?? []).filter((c) => !isFinishingSoon(c)).map((campaign) => (
                <CampaignCard key={campaign.id} campaign={campaign} sample={sample} onSaved={load} />
            ))}

            {(campaigns ?? []).some(isFinishingSoon) && (
                <>
                    <Typography variant="h6" sx={{ mt: 4, mb: 1 }}>Finishing soon</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 680 }}>
                        About 30 minutes before a customer's booked attraction ends: order dinner to the hotel, or dine in
                        at the nearest open dine-in venue. One per trip item, sharing the 20 h cap with the nudges above;
                        skipped in quiet hours, on Shabbat, when the venue is closed or the customer ordered in the last 2 h.
                    </Typography>
                    <FinishingSoonFireCard onFired={load} />
                    {(campaigns ?? []).filter(isFinishingSoon).map((campaign) => (
                        <CampaignCard key={campaign.id} campaign={campaign} sample={{ ...sample, restaurant: 'Moses Cafe Limassol' }} onSaved={load} />
                    ))}
                </>
            )}

            {(campaigns ?? []).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No campaigns configured.
                </Typography>
            )}
        </Box>
    );
};

export default NotificationsPage;
